<script setup lang="ts">
import { useForm } from "@inertiajs/vue3"
import { computed, ref, watch } from "vue"
import { ImagePlus, Plus, Trash2 } from "lucide-vue-next"
import { Button } from "@/components/ui/button"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { useToast } from "@/components/ui/toast/use-toast"
import { links } from "@/store"
import { toHuman } from "@/helpers/formatters/currency"
import IngredientSelect from "./IngredientSelect.vue"

type IngredientOption = {
    id: number
    name: string
    unit: string
    unit_cost_in_cents: number
}

type RecipeIngredient = {
    id: number
    quantity: number
    note?: string | null
}

type Recipe = {
    id: number
    name: string
    code?: string | null
    category: string
    description: string | null
    size: string
    is_iced?: boolean
    is_food?: boolean
    image_url?: string | null
    preparation?: string[]
    options?: string[]
    selling_price_in_cents: number
    ingredients: RecipeIngredient[]
    api: {
        update_path: string
        delete_path: string
    }
}

type IngredientRow = {
    ingredient_id: string
    quantity: string
    note: string
}

const props = defineProps<{
    open: boolean
    recipe?: Recipe | null
    ingredients: IngredientOption[]
    categories: { id: number; name: string; is_food: boolean }[]
}>()

const emit = defineEmits<{
    "update:open": [value: boolean]
}>()

const { toast } = useToast()

const categoryOptions = computed(() => props.categories.map((category) => category.name))

const isEditing = computed(() => !!props.recipe)

function emptyRow(): IngredientRow {
    return { ingredient_id: "", quantity: "", note: "" }
}

function rowsFromRecipe(recipe: Recipe | null | undefined): IngredientRow[] {
    if (!recipe?.ingredients?.length) {
        return [emptyRow()]
    }

    return recipe.ingredients.map((ingredient) => ({
        ingredient_id: String(ingredient.id),
        quantity: String(ingredient.quantity),
        note: ingredient.note ?? "",
    }))
}

function codeFromName(name: string): string {
    const words = name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .split(/\s+/)
        .filter(Boolean)

    if (words.length === 0) {
        return ""
    }

    if (words.length === 1) {
        return words[0].slice(0, 3).toUpperCase()
    }

    return words.map((word) => word.charAt(0).toUpperCase()).join("")
}

const lastAutoCode = ref("")
const imageInput = ref<HTMLInputElement | null>(null)
const selectedImageUrl = ref<string | null>(null)

const form = useForm({
    name: "",
    code: "",
    category: "Espresso",
    description: "",
    size: "16 oz",
    is_iced: false,
    selling_price: "",
    preparation: [""] as string[],
    options: [""] as string[],
    ingredients: [emptyRow()] as IngredientRow[],
    image: null as File | null,
})

const isFoodCategory = computed(() => Boolean(props.categories.find((category) => category.name === form.category)?.is_food))
const imagePreview = computed(() => {
    if (selectedImageUrl.value) {
        return selectedImageUrl.value
    }

    if (props.recipe?.image_url) {
        return props.recipe.image_url
    }

    return isFoodCategory.value ? "/images/food-placeholder.svg" : null
})

watch(
    () => [props.open, props.recipe] as const,
    ([open]) => {
        if (!open) {
            return
        }

        if (props.recipe) {
            lastAutoCode.value = codeFromName(props.recipe.name)
            form.code = props.recipe.is_food ? "" : (props.recipe.code ?? lastAutoCode.value).toUpperCase()
            form.name = props.recipe.name
            form.category = props.recipe.category
            form.description = props.recipe.description ?? ""
            form.size = props.recipe.size || (props.recipe.is_iced ? "22 oz" : "16 oz")
            form.is_iced = Boolean(props.recipe.is_iced)
            form.selling_price = String(props.recipe.selling_price_in_cents / 100)
            form.preparation = props.recipe.preparation?.length ? [...props.recipe.preparation] : [""]
            form.options = props.recipe.options?.length ? [...props.recipe.options] : [""]
            form.ingredients = rowsFromRecipe(props.recipe)
            form.image = null
        } else {
            lastAutoCode.value = ""
            form.reset()
            form.code = ""
            form.category = categoryOptions.value.includes("Espresso") ? "Espresso" : (categoryOptions.value[0] ?? "")
            form.size = "16 oz"
            form.is_iced = false
            form.preparation = [""]
            form.options = [""]
            form.ingredients = [emptyRow()]
            form.image = null
        }

        if (selectedImageUrl.value) {
            URL.revokeObjectURL(selectedImageUrl.value)
            selectedImageUrl.value = null
        }

        if (imageInput.value) {
            imageInput.value.value = ""
        }

        form.clearErrors()
    }
)

watch(
    () => form.is_iced,
    (isIced) => {
        if (isFoodCategory.value) {
            return
        }

        form.size = isIced ? "22 oz" : "16 oz"
    }
)

watch(isFoodCategory, (isFood) => {
    if (isFood) {
        form.code = ""
    } else if (!form.code) {
        form.code = codeFromName(form.name)
        lastAutoCode.value = form.code
    }
})

watch(
    () => form.name,
    (name) => {
        if (isFoodCategory.value) {
            form.code = ""
            return
        }

        const suggested = codeFromName(name)

        if (!form.code || form.code === lastAutoCode.value) {
            form.code = suggested
            lastAutoCode.value = suggested
        }
    }
)

const estimatedCost = computed(() => {
    return form.ingredients.reduce((total, row) => {
        const ingredient = props.ingredients.find((item) => String(item.id) === row.ingredient_id)
        const quantity = parseFloat(row.quantity)

        if (!ingredient || Number.isNaN(quantity)) {
            return total
        }

        return total + quantity * ingredient.unit_cost_in_cents
    }, 0)
})

function ingredientUnit(ingredientId: string) {
    return props.ingredients.find((item) => String(item.id) === ingredientId)?.unit ?? ""
}

function availableIngredients(currentId: string) {
    const selected = form.ingredients
        .map((row) => row.ingredient_id)
        .filter((id) => id && id !== currentId)

    return props.ingredients.filter((item) => !selected.includes(String(item.id)))
}

function addIngredientRow() {
    form.ingredients.push(emptyRow())
}

function removeIngredientRow(index: number) {
    if (form.ingredients.length === 1) {
        form.ingredients = [emptyRow()]
        return
    }

    form.ingredients.splice(index, 1)
}

function addListItem(field: "preparation" | "options") {
    form[field].push("")
}

function removeListItem(field: "preparation" | "options", index: number) {
    if (form[field].length === 1) {
        form[field] = [""]
        return
    }

    form[field].splice(index, 1)
}

function close() {
    emit("update:open", false)
}

function onImageChange(event: Event) {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0] ?? null

    form.image = file

    if (selectedImageUrl.value) {
        URL.revokeObjectURL(selectedImageUrl.value)
    }

    selectedImageUrl.value = file ? URL.createObjectURL(file) : null
}

function submitPayload(data: typeof form) {
    const payload: Record<string, unknown> = { ...data }

    if (!(payload.image instanceof File)) {
        delete payload.image
    }

    return payload
}

function submit() {
    const options = {
        preserveScroll: true,
        forceFormData: true,
        onSuccess() {
            close()
            toast({
                description: isEditing.value ? "Recipe updated." : "Recipe created.",
            })
        },
    }

    if (isEditing.value && props.recipe) {
        form.transform((data) => ({ ...submitPayload(data), _method: "put" })).post(props.recipe.api.update_path, options)
        return
    }

    form.transform(submitPayload).post(links.value.recipes.store_path, options)
}
</script>

<template>
    <Dialog :open="open" @update:open="emit('update:open', $event)">
        <DialogContent class="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden">
            <form class="flex min-h-0 flex-1 flex-col" @submit.prevent="submit">
                <DialogHeader>
                    <DialogTitle>{{ isEditing ? "Edit Recipe" : "New Recipe" }}</DialogTitle>
                    <DialogDescription>
                        {{ isEditing ? "Update recipe details, size, and preparation." : "Add a recipe card with ingredients, size, and preparation." }}
                    </DialogDescription>
                </DialogHeader>

                <div class="grid min-h-0 flex-1 gap-4 overflow-y-auto py-4">
                    <div class="grid gap-4" :class="isFoodCategory ? '' : 'sm:grid-cols-[minmax(0,1fr)_120px]'">
                        <FormField name="name">
                            <FormItem :error="form.errors.name">
                                <FormLabel>Name</FormLabel>
                                <FormControl>
                                    <Input v-model="form.name" placeholder="Honey Americano" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>

                        <FormField v-if="!isFoodCategory" name="code">
                            <FormItem :error="form.errors.code">
                                <FormLabel>Code</FormLabel>
                                <FormControl>
                                    <Input
                                        v-model="form.code"
                                        class="uppercase tracking-[0.18em]"
                                        maxlength="16"
                                        placeholder="HA"
                                        @input="form.code = form.code.toUpperCase()"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                    </div>

                    <FormField name="selling_price">
                        <FormItem :error="form.errors.selling_price">
                            <FormLabel>Selling Price</FormLabel>
                            <FormControl>
                                <Input v-model="form.selling_price" type="number" min="0" step="0.01" placeholder="165" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>

                    <div class="grid gap-4 sm:grid-cols-2">
                        <FormField name="category">
                            <FormItem :error="form.errors.category">
                                <FormLabel>Category</FormLabel>
                                <FormControl>
                                    <Select v-model="form.category">
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select category" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem v-for="category in categoryOptions" :key="category" :value="category">
                                                {{ category }}
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>

                        <FormField v-if="!isFoodCategory" name="is_iced">
                            <FormItem>
                                <FormLabel>Serve over ice</FormLabel>
                                <label class="flex h-10 items-center gap-2 text-sm">
                                    <input v-model="form.is_iced" type="checkbox" class="h-4 w-4 rounded border-gray-300" />
                                    Iced drink
                                </label>
                            </FormItem>
                        </FormField>
                    </div>

                    <FormField name="description">
                        <FormItem :error="form.errors.description">
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                                <Input v-model="form.description" placeholder="Short description of the drink" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>

                    <FormField v-if="isFoodCategory" name="image">
                        <FormItem :error="form.errors.image">
                            <FormLabel>Food photo</FormLabel>
                            <FormControl>
                                <div class="flex items-start gap-4">
                                    <button
                                        type="button"
                                        class="relative h-36 w-28 overflow-hidden rounded-sm border border-[rgb(181_115_5)] bg-[#f3f1ea]"
                                        @click="imageInput?.click()"
                                    >
                                        <img
                                            v-if="imagePreview"
                                            :src="imagePreview"
                                            alt="Food photo preview"
                                            class="h-full w-full"
                                            :class="form.image || props.recipe?.image_url ? 'object-cover' : 'bg-[#f7f7f5] object-contain'"
                                        />
                                        <span
                                            v-else
                                            class="flex h-full w-full items-center justify-center text-[rgb(181_115_5)]"
                                        >
                                            <ImagePlus class="h-6 w-6" />
                                        </span>
                                    </button>
                                    <div class="space-y-2 pt-1">
                                        <input
                                            ref="imageInput"
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp"
                                            class="hidden"
                                            @change="onImageChange"
                                        />
                                        <Button type="button" variant="outline" size="sm" @click="imageInput?.click()">
                                            <ImagePlus class="mr-1 h-4 w-4" />
                                            {{ form.image || props.recipe?.image_url ? "Change photo" : "Upload photo" }}
                                        </Button>
                                        <p class="text-xs text-muted-foreground">
                                            JPG, PNG, or WebP. Up to 5 MB. A plate placeholder is used until you add a photo.
                                        </p>
                                    </div>
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>

                    <Separator />

                    <div>
                        <div class="mb-3 flex items-center justify-between">
                            <div>
                                <h4 class="text-sm font-semibold">Ingredients</h4>
                                <p class="text-xs text-muted-foreground">
                                    {{ form.is_iced ? "Iced drinks are 22 oz only" : "Hot drinks are 16 oz only" }}: {{ toHuman(estimatedCost / 100) }}
                                </p>
                            </div>
                            <Button type="button" variant="outline" size="sm" @click="addIngredientRow">
                                <Plus class="mr-1 h-4 w-4" />
                                Add
                            </Button>
                        </div>

                        <p v-if="form.errors.ingredients" class="mb-2 text-sm text-destructive">
                            {{ form.errors.ingredients }}
                        </p>

                        <div class="space-y-3">
                            <div
                                v-for="(row, index) in form.ingredients"
                                :key="index"
                                class="grid grid-cols-1 gap-2 sm:grid-cols-[1.3fr_90px_1fr_36px] sm:items-end"
                            >
                                <FormField :name="`ingredients.${index}.ingredient_id`">
                                    <FormItem :error="form.errors[`ingredients.${index}.ingredient_id`]">
                                        <FormLabel v-if="index === 0" class="text-xs">Ingredient</FormLabel>
                                        <FormControl>
                                            <IngredientSelect
                                                v-model="row.ingredient_id"
                                                :options="availableIngredients(row.ingredient_id)"
                                                :ingredients="ingredients"
                                                :invalid="Boolean(form.errors[`ingredients.${index}.ingredient_id`])"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                </FormField>

                                <FormField :name="`ingredients.${index}.quantity`">
                                    <FormItem :error="form.errors[`ingredients.${index}.quantity`]">
                                        <FormLabel v-if="index === 0" class="text-xs">
                                            {{ form.is_iced ? "22 oz" : "16 oz" }}{{ ingredientUnit(row.ingredient_id) ? ` (${ingredientUnit(row.ingredient_id)})` : "" }}
                                        </FormLabel>
                                        <FormControl>
                                            <Input v-model="row.quantity" type="number" min="0.01" step="0.01" placeholder="0" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                </FormField>

                                <FormField :name="`ingredients.${index}.note`">
                                    <FormItem :error="form.errors[`ingredients.${index}.note`]">
                                        <FormLabel v-if="index === 0" class="text-xs">Note</FormLabel>
                                        <FormControl>
                                            <Input v-model="row.note" placeholder="Optional note" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                </FormField>

                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    class="text-muted-foreground hover:text-destructive"
                                    @click="removeIngredientRow(index)"
                                >
                                    <Trash2 class="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </div>

                    <Separator />

                    <div class="grid gap-4 sm:grid-cols-2">
                        <div>
                            <div class="mb-2 flex items-center justify-between">
                                <h4 class="text-sm font-semibold">Preparation</h4>
                                <Button type="button" variant="outline" size="sm" @click="addListItem('preparation')">
                                    <Plus class="mr-1 h-3.5 w-3.5" />
                                    Step
                                </Button>
                            </div>
                            <div class="space-y-2">
                                <div v-for="(_, index) in form.preparation" :key="`prep-${index}`" class="flex gap-2">
                                    <span class="mt-2 w-5 text-xs text-muted-foreground">{{ index + 1 }}.</span>
                                    <Input v-model="form.preparation[index]" placeholder="Add sauce and syrup to cup." />
                                    <Button type="button" variant="ghost" size="icon" @click="removeListItem('preparation', index)">
                                        <Trash2 class="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div>
                            <div class="mb-2 flex items-center justify-between">
                                <h4 class="text-sm font-semibold">Options</h4>
                                <Button type="button" variant="outline" size="sm" @click="addListItem('options')">
                                    <Plus class="mr-1 h-3.5 w-3.5" />
                                    Option
                                </Button>
                            </div>
                            <div class="space-y-2">
                                <div v-for="(_, index) in form.options" :key="`opt-${index}`" class="flex gap-2">
                                    <Input v-model="form.options[index]" placeholder="Top with sea salt." />
                                    <Button type="button" variant="ghost" size="icon" @click="removeListItem('options', index)">
                                        <Trash2 class="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" @click="close">Cancel</Button>
                    <Button type="submit" :disabled="form.processing" class="bg-[rgb(181_115_5)] hover:bg-[rgb(145_90_0)]">
                        {{ isEditing ? "Update Recipe" : "Create Recipe" }}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>
